import { ref, computed, watch } from 'vue';
import { useToast } from '@/composables/common/useToast';
import { useBaseDeportistaWizard, DEFAULT_PAIS } from './useBaseDeportistaWizard';
import { buscarBase as buscarBaseService } from '@/services/admin/bases/renovarDeportista';

const toast = useToast();

/**
 * Mapea los campos de la respuesta de buscarBase al formData reactivo.
 */
function mapBaseToFormData(base, formData) {
    formData.nombre = base.nombre || '';
    formData.apellidos = base.apellidos || '';
    formData.tipoDocumento = base.tipoDocumento || 'DNI';
    formData.dni = base.dni || '';
    formData.fecha_nacimiento = base.fecha_nacimiento || '';
    formData.fecha_caducidad_dni = base.fecha_caducidad_dni || '';
    formData.telefono = base.telefono || '';
    formData.codigo_pais = base.codigo_pais || '+34';
    formData.email = base.email || '';
    formData.pais_de_nacimiento = base.pais_de_nacimiento || { ...DEFAULT_PAIS };
    formData.provincia_nacimiento_id = base.provincia_nacimiento_id || null;
    formData.localidad_nacimiento_id = base.localidad_nacimiento_id || null;
    formData.nacionalidad = base.nacionalidad || 'Española';
    formData.direccion = base.direccion || '';
    formData.club_de_procedencia = base.club_de_procedencia || '';
    formData.lateralidad = base.lateralidad || null;

    formData.categoria_base_id = base.categoria_base?.id || base.categoria_base_id || null;
    formData.equipo_id = base.equipo?.id || base.equipo_id || null;

    // Responsables — soporta 3 formatos del API:
    //   1. sin _1 (legacy):     dni_responsable
    //   2. _1 en medio (camel): responsable_1_dni
    //   3. _1 al final:         dni_responsable_1  (formato real del API actual)
    formData.responsable_1_nombre = base.nombre_responsable || base.nombre_responsable_1 || '';
    formData.responsable_1_apellidos = base.apellidos_responsable || base.apellidos_responsable_1 || '';
    formData.responsable_1_tipoDocumento = base.tipo_documento_responsable || base.responsable_1_tipoDocumento || base.tipo_documento_responsable_1 || 'DNI';
    formData.responsable_1_dni = base.dni_responsable || base.responsable_1_dni || base.dni_responsable_1 || '';
    formData.responsable_1_telefono = base.telefono_responsable || base.responsable_1_telefono || base.telefono_responsable_1 || '';
    formData.responsable_1_callingCode = base.calling_code_responsable || base.responsable_1_callingCode || base.calling_code_responsable_1 || '+34';
    formData.responsable_1_email = base.email_responsable || base.responsable_1_email || base.email_responsable_1 || '';
    formData.responsable_1_fecha_nacimiento = base.fecha_nacimiento_responsable || base.responsable_1_fecha_nacimiento || base.fecha_nacimiento_responsable_1 || '';

    formData.responsable_2_nombre = base.nombre_responsable_2 || '';
    formData.responsable_2_apellidos = base.apellidos_responsable_2 || '';
    formData.responsable_2_tipoDocumento = base.responsable_2_tipoDocumento || '';
    formData.responsable_2_dni = base.dni_responsable_2 || base.responsable_2_dni || '';
    formData.responsable_2_telefono = base.telefono_responsable_2 || base.responsable_2_telefono || '';
    formData.responsable_2_callingCode = base.responsable_2_callingCode || '+34';
    formData.responsable_2_email = base.email_responsable_2 || '';
    formData.responsable_2_fecha_nacimiento = base.fecha_nacimiento_responsable_2 || '';

    formData.observaciones = base.observaciones || '';

    // CRÍTICO para renovación: guardar el id del BaseClub antiguo.
    // El backend (BaseClub.php:334) lee datos_comprador['base_id_antiguo']
    // y llama a sincronizarDatosBase() para copiar lesiones y valoración.
    formData.base_id_antiguo = base.id || base.base_id_antiguo;

    // Custom fields: wipe explícito en renovación (semántica consistente con
    // el frontend público RenovarBase.vue:192-194). El usuario debe tipear
    // los campos personalizados cada temporada, no se preservan del año previo.
    formData.fields_deportista = {};
    formData.fields_responsable_1 = {};
    formData.fields_responsable_2 = {};

    // Indumentarias ya seleccionadas
    const inds = base.indumentariasElegidas || base.categoria_base?.indumentariasElegidas || [];
    formData.indumentariasElegidas = Array.isArray(inds) ? inds : [];

    // Descuentos
    formData.descuento_aplicado = base.descuento_aplicado || 0;
    formData.descuento_integro = base.descuento_integro || 0;

    // Datos de la categoría (para el paso de confirmación)
    if (base.categoria_base) {
        formData.camiseta_nombre = base.categoria_base.camiseta_nombre || '';
        formData.camiseta_numero = base.categoria_base.camiseta_numero || '';
    }
}

/**
 * useRenovarDeportista — Orquestador del wizard de RENOVACIÓN de deportistas.
 * Delega toda la lógica compartida en useBaseDeportistaWizard.
 *
 * DIFERENCIAS con el base:
 *  - Steps incluye 'Buscar deportista' y NO incluye 'Documentación'
 *  - Añade buscarBase, buscarLoading, baseEncontrada, buscarError, volverABuscar
 *  - Override prevStep para resetear estado de búsqueda
 *  - Override loading para incluir buscarLoading
 *
 * @param {number} listaId
 * @returns {object}
 */
export function useRenovarDeportista(listaId) {
    const steps = ref([]);

    const base = useBaseDeportistaWizard(listaId, { steps });

    // ── Estado específico de renovar ──
    const buscarLoading = ref(false);
    const baseEncontrada = ref(false);
    const buscarError = ref('');

    // ── Steps dinámicos según categoría seleccionada ──
    watch(() => base.selectedCategoria.value, cat => {
        const s = [
            'Buscar deportista',
            'Jugador',
            'Responsables',
        ];

        if (cat?.tiene_indumentarias) {
            s.push('Indumentarias');
        }

        s.push('Descuentos', 'Confirmación');
        steps.value = s;
    }, { immediate: true });

    // ── Loading incluye buscarLoading ──
    const loading = computed(() =>
        base.categoriasLoading.value || base.equiposLoading.value || buscarLoading.value,
    );

    // ── PrevStep con reset de búsqueda ──
    function prevStep() {
        if (base.currentStep.value > 1) {
            base.currentStep.value--;
        }
        if (base.currentStep.value === 1) {
            baseEncontrada.value = false;
            buscarError.value = '';
        }
    }

    // ── API: Buscar base por DNI ──
    async function buscarBase(dni) {
        buscarLoading.value = true;
        base.setError(null);
        buscarError.value = '';
        try {
            const res = await buscarBaseService(listaId, dni);
            const found = res.data.base || res.data.data || res.data;
            if (!found) {
                buscarError.value = 'No se encontró un deportista con ese DNI.';
                return;
            }
            mapBaseToFormData(found, base.formData);

            // Setear categoría seleccionada
            if (found.categoria_base) {
                base.selectedCategoria.value = found.categoria_base;
            } else if (base.formData.categoria_base_id) {
                const cat = base.categorias.value.find(
                    c => c.id === base.formData.categoria_base_id,
                );
                if (cat) base.selectedCategoria.value = cat;
            }

            // Session ID existente
            if (found.session_id) {
                base.sessionId.value = found.session_id;
            }

            baseEncontrada.value = true;
            buscarError.value = '';
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(err);
            }
            const msg = err?.response?.data?.message
                || err?.response?.data?.error
                || 'Error al buscar el deportista.';
            buscarError.value = msg;
            toast.error(msg);
        } finally {
            buscarLoading.value = false;
        }
    }

    // ── Volver al paso de búsqueda ──
    function volverABuscar() {
        baseEncontrada.value = false;
        buscarError.value = '';
        base.currentStep.value = 1;
    }

    return {
        ...base,
        steps,
        loading,
        prevStep,
        buscarBase,
        buscarLoading,
        baseEncontrada,
        buscarError,
        volverABuscar,
    };
}
